import { formatDataValue, formatNumber } from 'utils/formatFns';

export const vehicleSpecsFields = {
  carryCapacity: {
    label: 'Carry Capacity',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.carryCapacity,
    formatValue: (value) => {
      if (!value) {
        return 'Not Yet Available';
      }
      return `${formatNumber(value, { maximumFractionDigits: 2 })} lbs`;
    },
  },
  doors: {
    label: 'Doors',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.doorsQuantity,
    formatValue: (value) => formatDataValue({ value }),
  },
  drivetrain: {
    label: 'Drivetrain',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.drivetrainName,
    formatValue: (value) => formatDataValue({ value }),
  },
  engineNumber: {
    label: 'Engine No.',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.engineNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  engineSource: {
    label: 'Engine Source',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.engineSource,
    formatValue: (value) => formatDataValue({ value }),
  },
  fuelType: {
    label: 'Fuel Type',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.fuelType,
    formatValue: (value) => formatDataValue({ value }),
  },
  gradeDescription: {
    label: 'Grade Description',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.gradeSpecName,
    formatValue: (value) => formatDataValue({ value }),
  },
  gradeSpecName: {
    label: 'Grade',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.gradeSpecName,
    formatValue: (value) => formatDataValue({ value }),
  },
  grossVehicleWeightRating: {
    label: 'Gross Vehicle Weight Rating',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.grossVehicleWeightRating,
    formatValue: (value) => formatDataValue({ value }),
  },
  gvwr: {
    label: 'GVWR',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.grossVehicleWeightPerUnitImp,
    formatValue: (value) => (value ? formatNumber(value) : 'N/A'),
  },
  horsepower: {
    label: 'Horsepower',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.horsepower,
    formatValue: (value) => formatDataValue({ value }),
  },
  keyCode1: {
    label: '#1',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.keyNumber,
    formatValue: (value) => formatDataValue({ value }),
    fieldName: 'keyNumber',
  },
  keyCode2: {
    label: '#2',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.keyCodeNumber2,
    formatValue: (value) => formatDataValue({ value }),
    fieldName: 'keyCodeNumber2',
  },
  measurement: {
    label: 'Measurement',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.measurementPerUnitImp,
    formatValue: (value) => {
      if (!value) {
        return 'N/A';
      }
      return `${formatNumber(value, { maximumFractionDigits: 2 })} lbs`;
    },
  },
  ngtAudioHeadUnit: {
    label: 'NGT Audio Head Unit',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.ngtAudioHeadUnit,
    formatValue: (value) => formatDataValue({ value }),
  },
  ngtHardware: {
    label: 'NGT Hardware',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.ngtHardwareType,
    formatValue: (value) => formatDataValue({ value }),
  },
  ngtMEID: {
    label: 'NGT MEID',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.ngtMEID,
    formatValue: (value) => formatDataValue({ value }),
  },
  numberOfCylinders: {
    label: 'Number of Cylinders',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.numberOfCylinders,
    formatValue: (value) => formatDataValue({ value }),
  },
  ppoWeight: {
    label: 'PPO Weight',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.ppoWeightImp,
    formatValue: (value) => {
      if (!value) {
        return 'N/A';
      }
      return `${formatNumber(value, { maximumFractionDigits: 2 })} lbs`;
    },
  },
  seats: {
    label: 'Seats',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.seatsNumber,
    formatValue: (value) => formatDataValue({ value, fallbackString: 'Not Yet Available' }),
  },
  shippingWeight: {
    label: 'Shipping Weight',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.shipWeight,
    formatValue: (value) => {
      if (!value) {
        return 'N/A';
      }
      return `${formatNumber(value, { maximumFractionDigits: 2 })} lbs`;
    },
  },
  style: {
    label: 'Style',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.bodyStyle,
    formatValue: (value) => formatDataValue({ value }),
  },
  tireCode1: {
    label: '#1',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.tire1Code,
    formatValue: (value) => formatDataValue({ value }),
  },
  tireCode2: {
    label: '#2',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.tire2Code,
    formatValue: (value) => formatDataValue({ value }),
  },
  tireCode1Header: {
    label: 'Tire Code #1',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.tire1Code,
    formatValue: (value) => formatDataValue({ value }),
  },
  tireCode2Header: {
    label: 'Tire Code #2',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.tire2Code,
    formatValue: (value) => formatDataValue({ value }),
  },
  transmissionNumber: {
    label: 'Transmission Number',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.transmissionCode,
    formatValue: (value) => formatDataValue({ value }),
  },
  vehicleGrade: {
    label: 'Vehicle Grade',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.vehicleGradeDescription,
    formatValue: (value) => formatDataValue({ value }),
  },
  vehicleHeight: {
    label: 'Vehicle Height',
    getValue: (data) => ({
      height: data?.generalInfo?.vehicleSpecs?.vehicleHeightPerUnitImp,
      uom: data?.generalInfo?.vehicleSpecs?.dimensionUOMImp,
    }),
    formatValue: ({ height, uom }) => {
      if (!height) {
        return 'N/A';
      }
      return `${formatNumber(height, { maximumFractionDigits: 2 })} ${
        uom?.toLowerCase() || 'inches'
      }`;
    },
  },
  vehicleLength: {
    label: 'Vehicle Length',
    getValue: (data) => ({
      length: data?.generalInfo?.vehicleSpecs?.vehicleLengthPerUnitImp,
      uom: data?.generalInfo?.vehicleSpecs?.dimensionUOMImp,
    }),
    formatValue: ({ length, uom }) => {
      if (!length) {
        return 'N/A';
      }
      return `${formatNumber(length, { maximumFractionDigits: 2 })} ${
        uom?.toLowerCase() || 'inches'
      }`;
    },
  },
  vehicleType: {
    label: 'Vehicle Type',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.vehicleType,
    formatValue: (value) => formatDataValue({ value }),
  },
  vehicleWidth: {
    label: 'Vehicle Width',
    getValue: (data) => ({
      width: data?.generalInfo?.vehicleSpecs?.vehicleWidthPerUnitImp,
      uom: data?.generalInfo?.vehicleSpecs?.dimensionUOMImp,
    }),
    formatValue: ({ width, uom }) => {
      if (!width) {
        return 'N/A';
      }
      return `${formatNumber(width, { maximumFractionDigits: 2 })} ${
        uom?.toLowerCase() || 'inches'
      }`;
    },
  },
  wheelbase: {
    label: 'Wheelbase',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.wheelbase,
    formatValue: (value) => formatDataValue({ value }),
  },
  xmRadioNumber: {
    label: 'XM Radio Number',
    getValue: (data) => data?.generalInfo?.vehicleSpecs?.xmRadioNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
};
